import React, { useMemo , useEffect} from 'react';
import clsx from 'clsx';
import { useInstanceId } from '@wordpress/compose';
import RadioControlOption from './option';
import './style.scss';
import { useSelect, useDispatch } from '@wordpress/data';
import { VALIDATION_STORE_KEY } from '@woocommerce/block-data';
import { ValidationInputError } from '@woocommerce/blocks-components';
import { __ } from '@wordpress/i18n';

const RadioControl = ( {
	className = '',
	id,
	selected = '',
	onChange,
	options = [],
	disabled = false,
	highlightChecked = false,
	title = '',
	required = false,
	errorMessage = __( 'Please select an option', 'woo-checkout-field-editor-pro' ),
    errorId: incomingErrorId,
} ) => {

	const instanceId = useInstanceId( RadioControl );
	const radioControlId = id || instanceId;
	const errorId = incomingErrorId || radioControlId;

	const { setValidationErrors, clearValidationError } = useDispatch( VALIDATION_STORE_KEY );

	const validationError = useSelect( ( select ) => {
        const store = select( VALIDATION_STORE_KEY );
        return store.getValidationError( errorId ) || { hidden: true };
    } );

	useEffect( () => {
        if ( ! required || selected ) {
            clearValidationError( errorId );
        } else {
            setValidationErrors( {
                [ errorId ]: {
                    message: errorMessage,
                    hidden: true,
                },
            } );
        }
        return () => {
            clearValidationError( errorId );
        };
    }, [ selected, required, errorId, errorMessage ] );

	const selectedOptionNumber = useMemo( () => {
		return options.findIndex( ( option ) => option.value === selected );
	}, [ options, selected ] );

	if ( !options.length ) {
		return null;
	}

	return (
		<div
			className={ clsx(
				'wc-block-components-radio-control thwcfe-radio-control',
				{
					'has-error': ! validationError.hidden,
					'wc-block-components-radio-control--highlight-checked--first-selected':
						highlightChecked && selectedOptionNumber === 0,
					'wc-block-components-radio-control--highlight-checked--last-selected':
						highlightChecked &&
						selectedOptionNumber === options.length - 1,
					'wc-block-components-radio-control--highlight-checked':
						highlightChecked,
				},
				className // Adding custom class
			) }
		>
			<div className="wc-block-components-cfe-radio-control__heading"> {title || ''} </div>
			{ options.map( ( option ) => (
				<RadioControlOption
					highlightChecked={ highlightChecked }
					key={ `${ radioControlId }-${ option.value }` }
					name={ `radio-control-${ radioControlId }` }
					checked={ option.value === selected }
					option={ option }
					onChange={ ( value ) => {
						onChange( value );
						if ( typeof option.onChange === 'function' ) {
							option.onChange( value );
						}
					} }
					disabled={ disabled }
				/>
			)) }
			<ValidationInputError propertyName={ errorId } />
		</div>
	);
};

export default RadioControl;
export { default as RadioControlOption } from './option';
export { default as RadioControlOptionLayout } from './option-layout';
